import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface Budget {
  id: string;
  name: string;
  description?: string;
  fiscalYear: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
  budgetItems: BudgetItem[];
}

interface BudgetItem {
  id: string;
  accountId: string;
  account: {
    code: string;
    name: string;
  };
  costCenterId?: string;
  costCenter?: {
    name: string;
    code?: string;
  };
  monthlyAmount: number;
  quarterlyAmount?: number;
  yearlyAmount: number;
  notes?: string;
}

interface Account {
  id: string;
  code: string;
  name: string;
  rootType: string;
}

interface CostCenter {
  id: string;
  name: string;
  code?: string;
}

interface BudgetVsActualData {
  accountId: string;
  accountCode: string;
  accountName: string;
  costCenterId?: string;
  costCenterName?: string;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
  period: string;
}

const Budgets: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showVsActualModal, setShowVsActualModal] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [budgetVsActualData, setBudgetVsActualData] = useState<BudgetVsActualData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fiscalYear: new Date().getFullYear().toString(),
    startDate: '',
    endDate: '',
    items: [] as Array<{
      accountId: string;
      costCenterId?: string;
      monthlyAmount: string;
      quarterlyAmount: string;
      yearlyAmount: string;
      notes: string;
    }>,
  });

  const [vsActualFormData, setVsActualFormData] = useState({
    periodStart: '',
    periodEnd: '',
    periodType: 'monthly' as 'monthly' | 'quarterly' | 'yearly',
  });

  useEffect(() => {
    fetchBudgets();
    fetchAccounts();
    fetchCostCenters();
  }, []);

  const fetchBudgets = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/erp/budgets');
      setBudgets(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching budgets:', err);
      toast.error('Failed to load budgets');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', isGroup: 'false' } });
      setAccounts(response.data.data || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers');
      setCostCenters(response.data.data || []);
    } catch (err) {
      console.error('Error fetching cost centers:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.items.length === 0) {
      setError('Please add at least one budget item');
      return;
    }

    try {
      const payload = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        items: formData.items.map(item => ({
          accountId: item.accountId,
          costCenterId: item.costCenterId || undefined,
          monthlyAmount: parseFloat(item.monthlyAmount) || 0,
          quarterlyAmount: item.quarterlyAmount ? parseFloat(item.quarterlyAmount) : undefined,
          yearlyAmount: parseFloat(item.yearlyAmount) || 0,
          notes: item.notes || undefined,
        })),
      };

      const response = await axios.post('/api/erp/budgets', payload);
      if (response.data.success) {
        toast.success('Budget created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchBudgets();
      } else {
        setError(response.data.error || 'Failed to create budget');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create budget';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleEdit = (budget: Budget) => {
    setSelectedBudget(budget);
    setFormData({
      name: budget.name,
      description: budget.description || '',
      fiscalYear: budget.fiscalYear,
      startDate: budget.startDate.split('T')[0],
      endDate: budget.endDate.split('T')[0],
      items: budget.budgetItems.map(item => ({
        accountId: item.accountId,
        costCenterId: item.costCenterId || '',
        monthlyAmount: item.monthlyAmount.toString(),
        quarterlyAmount: item.quarterlyAmount?.toString() || '',
        yearlyAmount: item.yearlyAmount.toString(),
        notes: item.notes || '',
      })),
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBudget) return;
    setError(null);

    try {
      const payload = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        items: formData.items.map(item => ({
          accountId: item.accountId,
          costCenterId: item.costCenterId || undefined,
          monthlyAmount: parseFloat(item.monthlyAmount) || 0,
          quarterlyAmount: item.quarterlyAmount ? parseFloat(item.quarterlyAmount) : undefined,
          yearlyAmount: parseFloat(item.yearlyAmount) || 0,
          notes: item.notes || undefined,
        })),
      };

      const response = await axios.put(`/api/erp/budgets/${selectedBudget.id}`, payload);
      if (response.data.success) {
        toast.success('Budget updated successfully');
        setShowEditModal(false);
        setSelectedBudget(null);
        resetForm();
        fetchBudgets();
      } else {
        setError(response.data.error || 'Failed to update budget');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update budget';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this budget?')) return;

    try {
      const response = await axios.delete(`/api/erp/budgets/${id}`);
      if (response.data.success) {
        toast.success('Budget deleted successfully');
        fetchBudgets();
      } else {
        toast.error(response.data.error || 'Failed to delete budget');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete budget');
    }
  };

  const handleViewVsActual = (budget: Budget) => {
    setSelectedBudget(budget);
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setVsActualFormData({
      periodStart: firstDay.toISOString().split('T')[0],
      periodEnd: lastDay.toISOString().split('T')[0],
      periodType: 'monthly',
    });
    setShowVsActualModal(true);
  };

  const handleGenerateVsActual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBudget) return;
    setError(null);

    try {
      const response = await axios.post(`/api/erp/budgets/${selectedBudget.id}/budget-vs-actual`, {
        periodStart: vsActualFormData.periodStart,
        periodEnd: vsActualFormData.periodEnd,
        periodType: vsActualFormData.periodType,
      });

      if (response.data.success) {
        setBudgetVsActualData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to generate report');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to generate report';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      fiscalYear: new Date().getFullYear().toString(),
      startDate: '',
      endDate: '',
      items: [],
    });
  };

  const addBudgetItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          accountId: '',
          costCenterId: '',
          monthlyAmount: '',
          quarterlyAmount: '',
          yearlyAmount: '',
          notes: '',
        },
      ],
    });
  };

  const removeBudgetItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateBudgetItem = (index: number, field: string, value: string) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading budgets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Budgets</h1>
          <PageHelp articleKey="erp_budget" />
        </div>
        <div>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage budgets, track actual vs budget performance
          </p>
        </div>
        {hasPermission('erp_financial_report', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            New Budget
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {budgets.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No budgets found. Create your first budget to get started.</p>
          {hasPermission('erp_financial_report', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Budget
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fiscal Year</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {budgets.map(budget => (
                <tr key={budget.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{budget.name}</div>
                    {budget.description && (
                      <div className="text-sm text-gray-500">{budget.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{budget.fiscalYear}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(budget.startDate).toLocaleDateString()} - {new Date(budget.endDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {budget.budgetItems.length} item(s)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      budget.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {budget.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewVsActual(budget)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Budget vs Actual
                    </button>
                    {hasPermission('erp_financial_report', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(budget)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_financial_report', 'delete') && (
                          <button
                            onClick={() => handleDelete(budget.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Budget</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fiscal Year *</label>
                  <input
                    type="text"
                    required
                    value={formData.fiscalYear}
                    onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Budget Items</label>
                  <button
                    type="button"
                    onClick={addBudgetItem}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Item
                  </button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="border rounded-md p-4 mb-2">
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account *</label>
                        <select
                          required
                          value={item.accountId}
                          onChange={(e) => updateBudgetItem(index, 'accountId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select Account</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Cost Center</label>
                        <select
                          value={item.costCenterId}
                          onChange={(e) => updateBudgetItem(index, 'costCenterId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">None</option>
                          {costCenters.map(cc => (
                            <option key={cc.id} value={cc.id}>{cc.code ? `${cc.code} - ` : ''}{cc.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Monthly Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.monthlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'monthlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Quarterly Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.quarterlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'quarterlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Yearly Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={item.yearlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'yearlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <input
                        type="text"
                        value={item.notes}
                        onChange={(e) => updateBudgetItem(index, 'notes', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBudgetItem(index)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Budget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar structure to Create Modal */}
      {showEditModal && selectedBudget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Budget</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedBudget(null); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Same form fields as create modal */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fiscal Year *</label>
                  <input
                    type="text"
                    required
                    value={formData.fiscalYear}
                    onChange={(e) => setFormData({ ...formData, fiscalYear: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Budget Items</label>
                  <button
                    type="button"
                    onClick={addBudgetItem}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Item
                  </button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="border rounded-md p-4 mb-2">
                    <div className="grid grid-cols-2 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Account *</label>
                        <select
                          required
                          value={item.accountId}
                          onChange={(e) => updateBudgetItem(index, 'accountId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select Account</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Cost Center</label>
                        <select
                          value={item.costCenterId}
                          onChange={(e) => updateBudgetItem(index, 'costCenterId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">None</option>
                          {costCenters.map(cc => (
                            <option key={cc.id} value={cc.id}>{cc.code ? `${cc.code} - ` : ''}{cc.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Monthly Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.monthlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'monthlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Quarterly Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.quarterlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'quarterlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Yearly Amount *</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={item.yearlyAmount}
                          onChange={(e) => updateBudgetItem(index, 'yearlyAmount', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">Notes</label>
                      <input
                        type="text"
                        value={item.notes}
                        onChange={(e) => updateBudgetItem(index, 'notes', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBudgetItem(index)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedBudget(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Update Budget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Budget vs Actual Modal */}
      {showVsActualModal && selectedBudget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Budget vs Actual: {selectedBudget.name}</h3>
              <button onClick={() => { setShowVsActualModal(false); setSelectedBudget(null); setBudgetVsActualData([]); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            {budgetVsActualData.length === 0 ? (
              <form onSubmit={handleGenerateVsActual} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Period Start *</label>
                    <input
                      type="date"
                      required
                      value={vsActualFormData.periodStart}
                      onChange={(e) => setVsActualFormData({ ...vsActualFormData, periodStart: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Period End *</label>
                    <input
                      type="date"
                      required
                      value={vsActualFormData.periodEnd}
                      onChange={(e) => setVsActualFormData({ ...vsActualFormData, periodEnd: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Period Type</label>
                    <select
                      value={vsActualFormData.periodType}
                      onChange={(e) => setVsActualFormData({ ...vsActualFormData, periodType: e.target.value as any })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowVsActualModal(false); setSelectedBudget(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Generate Report
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Center</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Variance %</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {budgetVsActualData.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.accountCode} - {row.accountName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {row.costCenterName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {row.budgetAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {row.actualAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                            row.variance < 0 ? 'text-red-600' : row.variance > 0 ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            {row.variance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                            row.variancePercent < 0 ? 'text-red-600' : row.variancePercent > 0 ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            {row.variancePercent.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => { setBudgetVsActualData([]); }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Generate Another
                  </button>
                  <button
                    onClick={() => { setShowVsActualModal(false); setSelectedBudget(null); setBudgetVsActualData([]); }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Budgets;
